import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FocusHeader } from '../components/FocusHeader';
import { StepIndicator } from '../components/StepIndicator';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase';
import { getWizardSteps } from '../lib/wizardSteps';
import { WIZARD_PATHS, sessionIdFromLocation, wizardUrl } from '../lib/wizardRoutes';
import { CheckCircle, XCircle } from 'lucide-react';

export function IdeaCheck() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, currentIdea, updateSession, refreshSession, loadSessionById, loading } = useSession();
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const sessionId = sessionIdFromLocation(location);

    if (sessionId && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadSessionById(sessionId);
    }
  }, [location.state?.sessionId, location.search]);


  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Blog idee"
          onBack={() => navigate('/dashboard')}
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  if (!session || !currentIdea) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Blog idee"
          onBack={() => navigate('/dashboard')}
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card text-center">
            <h1 className="text-2xl font-bold text-[#231F20] mb-4">Geen blog idee gevonden</h1>
            <p className="text-gray-600 mb-6">
              Er is geen blog idee beschikbaar. Dit kan gebeuren als de sessie verlopen is of als er nog geen idee is gegenereerd.
            </p>
            <a
              href="https://www.dibbi.nl"
              className="btn-primary inline-block"
            >
              Ga naar dibbi.nl
            </a>
          </div>
        </main>
      </div>
    );
  }

  const handleApprove = async () => {
    try {
      console.log('Approving idea:', currentIdea.id);

      await supabase
        .from('blog_ideas')
        .update({
          status: 'approved',
          decided_at: new Date().toISOString(),
        })
        .eq('id', currentIdea.id);

      console.log('Idea marked as approved, updating session...');

      await supabase
        .from('sessions')
        .update({
          state: 'idea_approved',
          approved_idea_id: currentIdea.id,
        })
        .eq('id', session.id);

      await updateSession({
        state: 'idea_approved',
        approved_idea_id: currentIdea.id,
      });
      await refreshSession();

      console.log('Navigating to article direction with approved idea...');
      navigate(wizardUrl(WIZARD_PATHS.direction, session.id));
    } catch (error) {
      console.error('Error approving idea:', error);
    }
  };

  const handleReject = () => {
    navigate('/idea-reject', {
      state: { sessionId: session.id }
    });
  };

  const steps = session ? getWizardSteps(session, 1) : getWizardSteps(null, 1);

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title="Blog idee"
        onBack={() => navigate('/dashboard')}
      />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <StepIndicator steps={steps} />
        <div className="space-y-6 mt-6">
          <div className="card space-y-4">
            <h1 className="text-3xl md:text-4xl font-bold text-[#231F20] leading-tight">
              {currentIdea.idea_title}
            </h1>

            <p className="text-lg text-gray-700 leading-relaxed">
              {currentIdea.idea_content}
            </p>

            {currentIdea.idea_reason && (
              <p className="text-sm text-gray-500 italic pt-2 border-t border-gray-100">
                {currentIdea.idea_reason}
              </p>
            )}
          </div>

          <div className="space-y-3">
            <button
              onClick={handleApprove}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              Goedkeuren - Schrijf artikel
            </button>

            <button
              onClick={handleReject}
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              <XCircle className="w-5 h-5" />
              Afwijzen - Nieuw idee proberen
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
